import { infiniteQueryOptions, mutationOptions, queryOptions } from '@tanstack/react-query';
import { NormalizeOAS, OASModel } from 'fets';

import { PortalApiClient } from '../../../rest-api/portalApiClient';
import type openapi from '../../../rest-api/openapi';
import { isStringEmptyOrWhiteSpace } from '../../../utils/isStringEmptyOrWhiteSpace';

type O = NormalizeOAS<typeof openapi>;

export type ProjectsCorePortalApiClient = Pick<
  PortalApiClient,
  | 'getMe'
  | 'getSubscriptionPlan'
  | 'listProjects'
  | 'getTags'
  | 'createTag'
  | 'updateTag'
  | 'deleteTag'
  | 'setTagToProject'
  | 'createProject'
  | 'createProjectFromTemplate'
  | 'duplicateProject'
  | 'deleteProject'
  | 'updateProjectProperties'
  | 'uploadThumbnail'
  | 'updateOnboarding'
  | 'getOnboarding'
  | 'listTemplates'
>;

export type ProjectsListingFilter = Omit<NonNullable<Parameters<PortalApiClient['listProjects']>[0]>, 'cursor'>;
export type CreateTagParams = OASModel<O, 'CreateTagUseCase'> & { projectGuidToAssign?: string };
export type TagTile = OASModel<O, 'TagTile'>;

const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  filteredList: (vars: ProjectsListingFilter) => [...projectKeys.all, 'list', vars] as const,
} as const;

export const tagKeys = {
  all: ['tags'] as const,
  lists: () => [...tagKeys.all, 'list'] as const,
} as const;

export const getListInfiniteProjectsQueryOptions = (
  portalApiClient: Pick<PortalApiClient, 'listProjects'>,
  vars: ProjectsListingFilter
) =>
  infiniteQueryOptions({
    queryKey: projectKeys.filteredList(vars),
    queryFn: async ({ pageParam }) => {
      const queryStrings = !isStringEmptyOrWhiteSpace(pageParam) ? { ...vars, cursor: pageParam } : vars;
      return await portalApiClient.listProjects(queryStrings);
    },
    getNextPageParam: (lastPage) => lastPage.nextPageCursor,
    initialPageParam: '',
  });

export const getListTagsQueryOptions = (portalApiClient: Pick<PortalApiClient, 'getTags'>) =>
  queryOptions({
    queryKey: tagKeys.lists(),
    queryFn: async () => await portalApiClient.getTags(),
  });

export const getCreateTagMutationOptions = (portalApiClient: Pick<PortalApiClient, 'createTag' | 'setTagToProject'>) =>
  mutationOptions({
    mutationFn: async (variables: CreateTagParams) => {
      const tag = await portalApiClient.createTag(variables);
      if (!tag.guid) {
        throw new Error('Created tag is missing guid');
      }

      if (variables.projectGuidToAssign) {
        await portalApiClient.setTagToProject({
          projectGuid: variables.projectGuidToAssign,
          tagGuid: tag.guid,
          assign: true,
        });
      }

      return { ...tag, guid: tag.guid };
    },
    onSuccess: (_data, _variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });

export const getDeleteTagMutationOptions = (portalApiClient: Pick<PortalApiClient, 'deleteTag'>) =>
  mutationOptions({
    mutationFn: portalApiClient.deleteTag,
    onSuccess: (_data, _variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });

export const getUpdateTagMutationOptions = (portalApiClient: Pick<PortalApiClient, 'updateTag'>) =>
  mutationOptions({
    mutationFn: portalApiClient.updateTag,
    onMutate: (variables, context) => {
      const previousTags = context.client.getQueryData<TagTile[]>(tagKeys.lists());

      if (previousTags) {
        const updatedTags = previousTags.map((tag) => {
          return tag.guid === variables.guid ? { ...tag, name: variables.name, color: variables.color } : tag;
        });
        context.client.setQueryData(tagKeys.lists(), updatedTags);
      }

      return { previousTags };
    },
    onError: (_error, _variables, onMutateResult, context) => {
      if (onMutateResult?.previousTags) {
        context.client.setQueryData(tagKeys.lists(), onMutateResult.previousTags);
      }
    },
  });

export const getSetTagToProjectMutationOptions = (portalApiClient: Pick<PortalApiClient, 'setTagToProject'>) =>
  mutationOptions({
    mutationFn: portalApiClient.setTagToProject,
  });
